import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import DashboardCore from "../../shared/dashboard/DashboardCore";
import RecentChats from "../../components/Dashboard/RecentChats";
import AnalyticsSummary from "../../components/Dashboard/AnalyticsSummary";
import ProfileViews from "../../components/Dashboard/ProfileViews";
// import MyArticles from "../../components/Dashboard/MyArticles";
import { Card, CardHeader, CardContent } from "../../components/ui/card";
import { Clock, Users, PenTool } from "lucide-react";
import DigitalDiary from "../../components/Sanctuary/DigitalDiary";
import SanctuaryWallModal from "../../components/Sanctuary/SanctuaryWallModal";

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
};

export default function DashboardProvider() {
    const { user } = useAuth();
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [showDiary, setShowDiary] = React.useState(false);
    const [showWall, setShowWall] = React.useState(false);

    const guardAction = (callback) => {
        if (user?.isGuest) {
            setShowWall(true);
        } else {
            callback();
        }
    };

    useEffect(() => {
        // Only redirect if they are NOT a provider role yet
        if (user && user.role !== "PROVIDER" && !user.provider) {
            navigate("/apply", { replace: true });
        }
    }, [user, navigate]);

    const [isOnline, setIsOnline] = React.useState(true);

    const toggleAvailability = () => {
        guardAction(() => {
            const nextState = !isOnline;
            setIsOnline(nextState);
            toast.success(`You are now ${nextState ? t('dash_online') : t('dash_offline')}`);
        });
    };

    return (
        <DashboardCore role="PROVIDER">
            <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* ACTION CENTER / AVAILABILITY */}
                <div className="lg:col-span-12">
                    <div className="flex flex-col bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 gap-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 dark:text-blue-400 shadow-inner">
                                    <Users className="w-5 h-5 md:w-6 md:h-6" />
                                </div>
                                <div className="leading-tight">
                                    <h3 className="font-display font-bold text-lg md:text-xl text-gray-900 dark:text-gray-100 uppercase tracking-tight">{t('dash_provider_console')}</h3>
                                    <p className="hidden xs:block text-[10px] md:text-xs text-gray-500 dark:text-gray-400 font-medium tracking-wide">{t('dash_presence_sub')}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-700/50 px-2 py-1.5 md:px-3 md:py-2 rounded-xl border border-gray-200 dark:border-gray-600">
                                <span className="hidden sm:inline text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none">{t('dash_availability')}</span>
                                <button
                                    onClick={toggleAvailability}
                                    className={`px-3 py-1 md:px-4 md:py-1.5 text-white text-[10px] font-black uppercase tracking-tighter rounded-lg shadow-sm transition-all duration-300 ${isOnline
                                        ? "bg-emerald-500 hover:bg-emerald-600 shadow-emerald-500/20"
                                        : "bg-slate-400 hover:bg-slate-500 shadow-slate-400/20"
                                        }`}
                                >
                                    {isOnline ? t('dash_online') : t('dash_offline')}
                                </button>
                            </div>
                        </div>

                        {/* SANCTUARY JOURNAL TRIGGER - DEDICATED ROW ON MOBILE */}
                        <button
                            onClick={() => guardAction(() => setShowDiary(true))}
                            className="flex items-center justify-center gap-3 px-5 py-3 md:py-2.5 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-xl border border-amber-200/50 dark:border-amber-800 hover:bg-amber-100/50 transition-all shadow-sm active:scale-95 group"
                        >
                            <PenTool className="w-4 h-4 md:w-5 h-5 group-hover:rotate-12 transition-transform" />
                            <div className="text-left">
                                <div className="text-xs font-black uppercase tracking-widest leading-none">{t('diary_pen_down')}</div>
                                <div className="text-[10px] font-medium text-amber-600/60 dark:text-amber-400/40 hidden xs:block">{t('dash_record_reflections')}</div>
                            </div>
                        </button>
                    </div>
                </div>

                {/* Left: Primary Work (Chats) */}
                <div className="lg:col-span-8 space-y-6">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-1 h-[500px]">
                        <RecentChats />
                    </div>
                </div>

                {/* Right: Insights & Analytics */}
                <div className="lg:col-span-4 space-y-6">
                    <Card className="bg-brand-blue/5 border-brand-blue/20">
                        <CardHeader><h4>{t('dash_impact')}</h4></CardHeader>
                        <CardContent><AnalyticsSummary /></CardContent>
                    </Card>
                    <Card>
                        <CardHeader><h3>{t('dash_profile_views')}</h3></CardHeader>
                        <CardContent><ProfileViews /></CardContent>
                    </Card>
                </div>
            </motion.div>

            {/* DIARY MODAL */}
            {showDiary && <DigitalDiary onClose={() => setShowDiary(false)} />}
            <SanctuaryWallModal isOpen={showWall} onClose={() => setShowWall(false)} />
        </DashboardCore>
    );
}
